/**
 * ForgotPasswordPage — request a password reset link.
 *
 * The backend returns the same response shape for known and unknown
 * emails to avoid enumeration, so the UI also lands on the same
 * "check your email" confirmation either way. A user who mistyped
 * their address sees the success page, then notices no email arrives.
 *
 * NFR-F-VAL-001: the email field uses React Hook Form + Zod for per-field,
 * on-blur validation (required + format). This is purely client-side and
 * leaks nothing about account existence, so the anti-enumeration behavior
 * — uniform confirmation on any non-5xx response — is unchanged.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const forgotSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address."),
});
type ForgotForm = z.infer<typeof forgotSchema>;

// -- Field error message component (mirrors UserManagementPage)
function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-brand-error mt-1">{message}</p>;
}

export function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Email shown on the confirmation view — captured at submit time.
  const [submittedEmail, setSubmittedEmail] = useState("");
  // Server/network error banner — distinct from inline field validation.
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<ForgotForm>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: "" },
    mode: "onBlur",
  });

  const email = form.watch("email");

  const onSubmit = async (data: ForgotForm) => {
    setIsLoading(true);
    setServerError(null);
    try {
      // Fire-and-display: regardless of the body, the backend returns
      // 200 with a uniform message. We only need to know the request
      // completed without a network error before swapping the view.
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email }),
      });
      if (!res.ok && res.status >= 500) {
        throw new Error("Server error");
      }
      setSubmittedEmail(data.email);
      setSubmitted(true);
    } catch {
      setServerError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-brand-navy">
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-lg bg-brand-crimson flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">XYDEN</span>
          </div>
          <p className="text-slate-400 text-sm">Leadership Maturity Assessment Platform</p>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8">
          {submitted ? (
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Check your email</h1>
              <p className="text-sm text-slate-600 mt-2">
                If an account exists for <span className="font-medium">{submittedEmail.trim()}</span>, a password reset
                link has been sent. The link is valid for one hour.
              </p>
              <p className="text-sm text-slate-500 mt-3">
                Didn't receive it? Check your spam folder, or try again in a few minutes.
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-crimson hover:underline"
                >
                  <ArrowLeft size={14} aria-hidden="true" /> Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-semibold text-slate-900">Forgot your password?</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Enter the email associated with your account and we'll send you a reset link.
                </p>
              </div>

              {serverError && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium"
                >
                  {serverError}
                </div>
              )}

              <form
                onSubmit={form.handleSubmit(onSubmit)}
                noValidate
                aria-label="Forgot password"
                className="space-y-4"
              >
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    aria-label="Email"
                    {...form.register("email", {
                      onChange: () => {
                        if (serverError) setServerError(null);
                      },
                    })}
                    className={`w-full h-9 rounded-lg border bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson ${
                      form.formState.errors.email ? "border-brand-error" : "border-brand-steel/50"
                    }`}
                    placeholder="you@example.com"
                  />
                  <FieldError message={form.formState.errors.email?.message} />
                </div>

                <button
                  type="submit"
                  disabled={!email.trim() || isLoading}
                  className="w-full py-2.5 bg-brand-crimson text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-crimson/90"
                >
                  {isLoading ? "Sending..." : "Send reset link"}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-crimson hover:underline"
                >
                  <ArrowLeft size={14} aria-hidden="true" /> Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

export default ForgotPasswordPage;
